import type { DiagnosisConfig } from './types';
import { illustrations } from '../content/illustrations';

export const diagnosisConfig: DiagnosisConfig = {
  issues: [
    {
      id: 'link-open',
      code: 'OPEN',
      marker: '01',
      title: '链接打不开或显示 404',
      summary: '页面空白、404，或者跳到了不认识的页面。',
      startNodeId: 'correct-account',
    },
    {
      id: 'region',
      code: 'REGION',
      marker: '02',
      title: '提示当前地区不支持',
      summary: '兑换页或会员页提示所在地、国家或地区不可用。',
      startNodeId: 'gemini-access',
    },
    {
      id: 'link-used',
      code: 'USED',
      marker: '03',
      title: '提示链接已使用',
      summary: '看到“已使用”或“需要新的激活链接”。',
      startNodeId: 'used-after-success',
    },
    {
      id: 'activated-unusable',
      code: 'ACTIVE',
      marker: '04',
      title: '显示会员，但功能用不了',
      summary: '会员已经出现，但 Gemini 加载、对话或页面异常。',
      startNodeId: 'entitlement-visible',
    },
    {
      id: 'third-party',
      code: 'TOOL',
      marker: '05',
      title: 'API、AI Studio 或其他工具不能用',
      summary: '会员正常，但开发者平台或其他工具仍有限制。',
      startNodeId: 'third-party-kind',
    },
    {
      id: 'subscription-dropped',
      code: 'DROP',
      marker: '06',
      title: '之前能用，现在会员消失了',
      summary: '已经激活过，后来权益或存储空间不见了。',
      startNodeId: 'dropped-evidence',
    },
    {
      id: 'account-choice',
      code: 'ACCOUNT',
      marker: '07',
      title: '不知道该用哪个账号',
      summary: '账号已有会员、家庭组、绑卡或多个账号不确定。',
      startNodeId: 'account-existing-plan',
    },
    {
      id: 'gemini-error',
      code: 'GEMINI',
      marker: '08',
      title: 'Gemini 显示“出了点问题”',
      summary: '页面显示 Something went wrong、出了点问题或请稍后再试。',
      startNodeId: 'gemini-generic-error',
    },
  ],
  nodes: {
    'correct-account': {
      id: 'correct-account',
      eyebrow: '先确认账号',
      title: '打开链接前，浏览器里登录的是准备兑换的账号吗？',
      description: '多个账号同时登录时，页面可能默认选择第一个账号。',
      summaryKey: 'account',
      help: {
        title: '怎么确认？',
        body: '打开谷歌账号页面，查看右上角头像对应的邮箱。不要把邮箱或密码发给任何人。',
        defaultOpen: true,
        href: 'https://myaccount.google.com/',
        hrefLabel: '打开谷歌账号页面',
        illustration: illustrations.account,
      },
      options: [
        { value: 'correct', label: '是，已经确认账号', nextNodeId: 'link-current-message' },
        {
          value: 'unsure',
          label: '不确定或登录了多个账号',
          outcomeId: 'switch-account-first',
        },
      ],
    },
    'link-current-message': {
      id: 'link-current-message',
      eyebrow: '观察页面',
      title: '重新打开原链接后，现在看到什么？',
      summaryKey: 'pageMessage',
      help: {
        title: '分不清页面提示？',
        body: '先看页面上的原文。打不开、404、已使用和跳转登录页是不同情况。',
        defaultOpen: true,
        illustration: illustrations.errors,
      },
      options: [
        { value: 'opens', label: '页面已经正常打开', outcomeId: 'verify-and-receive' },
        { value: '404', label: '仍然是 404 或空白页', outcomeId: 'link-needs-review' },
        { value: 'login', label: '跳到登录页', outcomeId: 'switch-account-first' },
        { value: 'used', label: '提示已使用或需要新链接', outcomeId: 'link-needs-review' },
      ],
    },
    'gemini-access': {
      id: 'gemini-access',
      eyebrow: '步骤 1 · 网页访问',
      title: '当前账号能正常打开 Gemini 网页吗？',
      summaryKey: 'geminiAccess',
      help: {
        title: '只检查能否打开',
        body: '这里不要求会员已经生效，只看网页是否能加载。',
        defaultOpen: true,
        href: 'https://gemini.google.com/',
        hrefLabel: '打开 Gemini 网页',
      },
      options: [
        { value: 'yes', label: '可以正常打开', nextNodeId: 'account-country-association' },
        { value: 'no', label: '打不开或也提示地区不可用', nextNodeId: 'detected-region' },
      ],
    },
    'detected-region': {
      id: 'detected-region',
      eyebrow: '步骤 2 · 网络识别',
      title: '在谷歌搜索页最底部，系统识别的是哪个地区？',
      description: '请以页面实际显示为准，不以节点名称或自认为的网络地区为准。',
      summaryKey: 'detectedRegion',
      help: {
        title: '在哪里看？',
        body: '打开谷歌搜索任意内容，滑到页面最底部，查看地区文字。',
        defaultOpen: true,
        example: '示例：页面底部显示「中国」或「United States」。',
        href: 'https://www.google.com/search?q=test',
        hrefLabel: '打开谷歌搜索',
        illustration: illustrations.region,
      },
      options: [
        { value: 'china', label: '显示中国', outcomeId: 'network-first' },
        { value: 'supported', label: '显示其他地区，但 Gemini 仍打不开', outcomeId: 'network-first' },
        { value: 'unknown', label: '找不到或无法识别', outcomeId: 'network-first' },
      ],
    },
    'account-country-association': {
      id: 'account-country-association',
      eyebrow: '步骤 2 · 账号关联地区',
      title: 'Google 官方条款页显示的账号关联国家或地区是中国吗？',
      description: '如果还没换过可用节点重试原链接，请先试一次；仍失败再核对账号地区。显示中国不代表一定不能激活。',
      summaryKey: 'accountCountry',
      help: {
        title: '在哪里查看账号关联地区？',
        body: '打开 Google 官方条款页，登录目标账号后查看“国家/地区版本”或账号关联地区。这里只查看，不需要提供邮箱、手机号或其他账号信息。',
        defaultOpen: true,
        href: 'https://policies.google.com/terms',
        hrefLabel: '打开 Google 官方条款页',
        illustration: illustrations.countryAssociation,
      },
      options: [
        {
          value: 'not-china',
          label: '不是中国',
          nextNodeId: 'payment-profile',
        },
        {
          value: 'china',
          label: '显示中国',
          nextNodeId: 'payment-profile-before-country-action',
        },
        { value: 'unknown', label: '找不到或不确定', nextNodeId: 'payment-profile' },
      ],
    },
    'payment-profile-before-country-action': {
      id: 'payment-profile-before-country-action',
      eyebrow: '步骤 3 · 付款资料',
      title: '这个账号以前绑定过国内银行卡或国内付款资料吗？',
      description: '账号地区显示中国并不代表必须先修改。先确认付款资料；其他检查后还可换未试过的节点重试原链接。',
      summaryKey: 'paymentProfile',
      warning: '这里只确认现状。不要因为地区显示中国就直接修改地区或关闭仍在使用的付款资料。',
      help: {
        title: '只查看，不要急着删除',
        body: '打开官方付款设置确认是否存在资料。关闭资料可能影响现有订阅、余额和付款服务。',
        defaultOpen: true,
        href: 'https://payments.google.com/gp/w/home/settings',
        hrefLabel: '查看谷歌付款设置',
        illustration: illustrations.payment,
      },
      options: [
        { value: 'yes', label: '有国内付款资料', outcomeId: 'country-association-review' },
        { value: 'no', label: '没有绑定过', outcomeId: 'country-association-review' },
        { value: 'unknown', label: '不确定', outcomeId: 'country-association-review' },
      ],
    },
    'payment-profile': {
      id: 'payment-profile',
      eyebrow: '步骤 3 · 付款资料',
      title: '这个账号以前绑定过国内银行卡或国内付款资料吗？',
      summaryKey: 'paymentProfile',
      warning: '删除付款资料可能影响现有订阅、余额和付款服务。优先考虑使用符合条件的其他账号。',
      help: {
        title: '只查看，不要急着删除',
        body: '先打开官方付款设置确认是否存在资料。没有理解影响前，不要删除任何内容。',
        defaultOpen: true,
        href: 'https://payments.google.com/gp/w/home/settings',
        hrefLabel: '查看谷歌付款设置',
        illustration: illustrations.payment,
      },
      options: [
        { value: 'yes', label: '有国内付款资料', nextNodeId: 'payment-profile-action' },
        { value: 'no', label: '没有绑定过', outcomeId: 'account-alternative' },
        { value: 'unknown', label: '不确定', outcomeId: 'account-alternative' },
      ],
    },
    'payment-profile-action': {
      id: 'payment-profile-action',
      eyebrow: '付款资料处理',
      title: '你准备怎么处理这份付款资料？',
      description: '关闭整个付款资料可能影响订阅、余额、交易记录和付款服务。',
      warning: '如果仍在使用这份资料，或不清楚关闭影响，不要关闭。',
      options: [
        { value: 'switch', label: '换用没有冲突的其他账号', outcomeId: 'switch-account-first' },
        { value: 'close', label: '资料不再使用，查看关闭位置', outcomeId: 'payment-profile-close-guide' },
        { value: 'unknown', label: '不确定是否还在使用', outcomeId: 'switch-account-first' },
      ],
    },
    'gemini-generic-error': {
      id: 'gemini-generic-error',
      eyebrow: '先核对提示文字',
      title: '页面是否显示“Something went wrong”或“出了点问题”？',
      description: '这个通用报错和“当前地区不支持”不是同一种问题。请以页面原文为准。',
      summaryKey: 'pageMessage',
      options: [
        { value: 'yes', label: '是，就是这个提示', outcomeId: 'gemini-web-recovery' },
        { value: 'region', label: '不是，显示当前地区不支持', outcomeId: 'open-region-diagnosis' },
        { value: 'other', label: '都不是，是其他提示', outcomeId: 'gemini-service-boundary' },
      ],
    },
    'used-after-success': {
      id: 'used-after-success',
      eyebrow: '确认是否已生效',
      title: '出现“已使用”之前，页面是否已经提示兑换成功？',
      summaryKey: 'pageMessage',
      help: {
        title: '怎么区分？',
        body: '“链接已使用”表示链接状态；404 或空白页表示页面没有正常打开。',
        defaultOpen: true,
        illustration: illustrations.errors,
      },
      options: [
        { value: 'yes', label: '是，之前已经提示成功', outcomeId: 'used-is-normal' },
        { value: 'no', label: '没有成功过，第一次打开就这样', outcomeId: 'link-needs-review' },
        { value: 'unknown', label: '不确定', outcomeId: 'link-needs-review' },
      ],
    },
    'entitlement-visible': {
      id: 'entitlement-visible',
      eyebrow: '先分清问题',
      title: '在会员方案页或存储空间里，能看到会员权益吗？',
      summaryKey: 'existingMembership',
      help: {
        title: '应该看哪里？',
        body: '打开会员方案页，确认是否显示会员方案；也可以查看存储空间是否为 5TB。',
        defaultOpen: true,
        href: 'https://one.google.com/',
        hrefLabel: '打开会员方案页',
        illustration: illustrations.membership,
      },
      options: [
        { value: 'yes', label: '能看到会员或 5TB 存储', nextNodeId: 'unusable-surface' },
        { value: 'no', label: '看不到任何会员权益', outcomeId: 'activation-needs-review' },
      ],
    },
    'unusable-surface': {
      id: 'unusable-surface',
      eyebrow: '确认具体功能',
      title: '现在不能使用的是哪一部分？',
      summaryKey: 'thirdParty',
      options: [
        { value: 'gemini', label: 'Gemini 网页加载或对话异常', outcomeId: 'network-after-active' },
        { value: 'api', label: 'API、AI Studio 或其他工具', outcomeId: 'third-party-boundary' },
      ],
    },
    'third-party-kind': {
      id: 'third-party-kind',
      eyebrow: '确认使用范围',
      title: '你遇到问题的是哪种服务？',
      summaryKey: 'thirdParty',
      options: [
        { value: 'api', label: 'API 或开发者配额', outcomeId: 'third-party-boundary' },
        { value: 'studio', label: 'AI Studio', outcomeId: 'third-party-boundary' },
        { value: 'antigravity', label: '反重力', outcomeId: 'third-party-boundary' },
        { value: 'other', label: '其他第三方客户端', outcomeId: 'third-party-boundary' },
      ],
    },
    'dropped-evidence': {
      id: 'dropped-evidence',
      eyebrow: '确认当前状态',
      title: '会员方案页和存储空间现在都看不到原来的权益吗？',
      summaryKey: 'existingMembership',
      help: {
        title: '先检查这两处',
        body: '聊天或某个工具不能用，不等于会员消失。请以会员方案和存储空间为准。',
        defaultOpen: true,
        href: 'https://one.google.com/',
        hrefLabel: '打开会员方案页',
        illustration: illustrations.membership,
      },
      options: [
        { value: 'gone', label: '是，权益已经消失', outcomeId: 'drop-needs-review' },
        { value: 'visible', label: '还能看到会员或 5TB', outcomeId: 'network-after-active' },
        { value: 'unknown', label: '还没有检查', outcomeId: 'check-entitlement-first' },
      ],
    },
    'account-existing-plan': {
      id: 'account-existing-plan',
      eyebrow: '账号选择',
      title: '准备使用的账号目前属于哪种情况？',
      summaryKey: 'existingMembership',
      options: [
        { value: 'none', label: '没有会员，也不在家庭组', nextNodeId: 'account-payment-history' },
        { value: 'member', label: '已有会员或订阅', outcomeId: 'existing-plan-warning' },
        { value: 'family', label: '在家庭组或受他人管理', outcomeId: 'existing-plan-warning' },
        { value: 'unknown', label: '不确定', outcomeId: 'account-check-first' },
      ],
    },
    'account-payment-history': {
      id: 'account-payment-history',
      eyebrow: '最后一项',
      title: '账号是否绑定过国内付款资料？',
      summaryKey: 'paymentProfile',
      warning: '不要为了兑换贸然删除仍在使用的付款资料。',
      options: [
        { value: 'no', label: '没有绑定过', outcomeId: 'account-ready' },
        { value: 'yes', label: '绑定过', nextNodeId: 'payment-profile-action' },
        { value: 'unknown', label: '不确定', outcomeId: 'account-check-first' },
      ],
    },
  },
  outcomes: {
    'verify-and-receive': {
      id: 'verify-and-receive',
      code: 'OK01',
      kind: 'resolved',
      title: '页面已经恢复，可以继续兑换',
      body: '按页面提示完成兑换，再打开会员方案页或查看存储空间确认权益已经生效。',
      recommendation: '确认会员或 5TB 已出现后，返回闲鱼确认收货。',
      primaryAction: { label: '打开会员方案页', href: 'https://one.google.com/' },
      checklist: ['确认登录的是目标账号', '确认会员或 5TB 已出现', '返回闲鱼确认收货'],
    },
    'switch-account-first': {
      id: 'switch-account-first',
      code: 'ACT01',
      kind: 'self_action',
      title: '先切换到准备兑换的账号',
      body: '退出其他账号，或在同一浏览器里明确切换到目标账号，再重新打开原兑换链接。',
      recommendation: '不要把邮箱、密码或验证码发给任何人。',
      primaryAction: { label: '检查谷歌账号', href: 'https://myaccount.google.com/' },
      fallbackOutcomeId: 'link-needs-review',
      fallbackLabel: '已经切换账号，仍没解决',
    },
    'link-needs-review': {
      id: 'link-needs-review',
      code: 'LINK01',
      kind: 'seller_review',
      title: '需要卖家核实链接状态',
      body: '先不要继续点击或反复打开链接。请复制诊断摘要发给卖家，等待核实是否需要更换链接。',
      recommendation: '发送摘要即可，不要发送账号密码。',
    },
    'network-first': {
      id: 'network-first',
      code: 'NET01',
      kind: 'self_action',
      title: '先处理当前网络或地区识别',
      body: 'Gemini 网页本身无法正常访问，谷歌搜索页底部的地区信息可用于确认当前网络被如何识别。此时更换兑换链接通常不能解决问题。',
      recommendation: '先使用稳定且受支持地区的正常网络，确认 Gemini 可以打开后，再从原链接重试。不要频繁切换账号和地区。',
      fallbackOutcomeId: 'self-action-needs-review',
      fallbackLabel: '已经重新检查，仍没解决',
    },
    'gemini-web-recovery': {
      id: 'gemini-web-recovery',
      code: 'WEB01',
      kind: 'self_action',
      title: '再试一次 Gemini 网页恢复方法',
      body: '有些账号能打开 Gemini，但主页面仍提示地区不可用。可以尝试从谷歌官方的 Gem 创建页进入；这是进阶排查方法，不保证所有账号都能恢复。',
      recommendation: '请使用准备激活的同一个账号操作。能在预览窗口正常收到回复后，再返回 Gemini 主页面确认是否恢复。',
      primaryAction: { label: '打开 Gem 创建页', href: 'https://gemini.google.com/gems/create' },
      checklist: [
        '随便填写一个测试名称并保存',
        '在预览窗口发送 Hi',
        '收到正常回复后刷新 Gemini 主页面',
        '主页面恢复后再回来下单',
      ],
      fallbackOutcomeId: 'gemini-service-boundary',
      fallbackLabel: '已经尝试，仍然不能使用',
    },
    'open-region-diagnosis': {
      id: 'open-region-diagnosis',
      code: 'REGION01',
      kind: 'resolved',
      title: '请改用地区问题排查',
      body: '页面明确显示当前地区不支持，应按账号、网络识别和付款资料逐项检查。',
      recommendation: '打开地区问题排查页，并按页面实际情况选择。',
      primaryAction: { label: '打开地区问题排查', href: '/diagnose/region' },
    },
    'payment-profile-close-guide': {
      id: 'payment-profile-close-guide',
      code: 'PAY02',
      kind: 'self_action',
      title: '在付款中心找到“关闭支付资料”',
      body: '进入 Google 付款中心的“设置”，向下找到“支付资料状态”，其中会显示“关闭支付资料”。',
      recommendation: '只有在确认该资料没有仍在使用的订阅、余额或付款服务时才继续。关闭后重新登录目标账号，再打开原兑换链接。',
      illustration: illustrations.paymentProfileCloseExample,
      primaryAction: { label: '打开付款中心设置', href: 'https://payments.google.com/gp/w/home/settings' },
      checklist: [
        '确认当前登录的是准备兑换的账号',
        '确认现有订阅、余额和付款服务不再需要',
        '在“设置 → 支付资料状态”查找关闭入口',
        '关闭后重新登录，再打开原链接',
      ],
      fallbackOutcomeId: 'payment-profile-closed-still-failed',
      fallbackLabel: '已关闭并重新尝试，仍无法兑换',
    },
    'country-association-review': {
      id: 'country-association-review',
      code: 'COUNTRY01',
      kind: 'self_action',
      title: '账号地区显示中国，先看兑换页实际提示',
      body: '显示中国并不代表一定要修改账号地区。先用尚未尝试的可用节点重试原链接；若仍明确提示账号资格不符，才考虑官方地区关联申请。',
      recommendation: '申请调整是可选的后续排查，是否通过以 Google 审核为准，不保证调整后一定能激活。完成账号和付款资料检查后，再换未试过的可用节点重试原链接。',
      illustration: illustrations.countryAssociationFormExample,
      primaryAction: {
        label: '打开官方地区关联申请',
        href: 'https://policies.google.com/country-association-form?source=policies-site',
      },
      checklist: [
        '先确认条款页显示的账号关联地区',
        '先换可用节点重新打开原激活链接',
        '仍提示资格不符时，按需打开官方表单提交申请',
        '不要反复提交相同申请',
        '其他检查完成后，换未试过的可用节点再试原链接',
      ],
      fallbackOutcomeId: 'self-action-needs-review',
      fallbackLabel: '已经收到审核结果，仍然不能兑换',
    },
    'account-alternative': {
      id: 'account-alternative',
      code: 'ACT02',
      kind: 'seller_review',
      title: '常见原因已排查，需要进一步核实',
      body: '账号关联地区不受支持，或其他常见账号条件没有发现可自行修复的问题。请复制诊断摘要发给卖家。',
      recommendation: '卖家会根据兑换页面提示判断是否需要换账号、换链接或协商退款。',
    },
    'used-is-normal': {
      id: 'used-is-normal',
      code: 'OK02',
      kind: 'resolved',
      title: '成功后显示“已使用”是正常的',
      body: '兑换链接只能使用一次。已经成功兑换后再次打开，通常会显示链接已使用。',
      recommendation: '打开会员方案页或查看存储空间，确认权益后返回闲鱼确认收货。',
      primaryAction: { label: '检查会员权益', href: 'https://one.google.com/' },
    },
    'activation-needs-review': {
      id: 'activation-needs-review',
      code: 'ACTIVE01',
      kind: 'seller_review',
      title: '暂时看不到会员权益',
      body: '请不要继续切换账号或反复点击链接。复制诊断摘要发给卖家核实首次兑换是否成功。',
      recommendation: '同时保留会员方案页或存储空间的完整截图。',
    },
    'network-after-active': {
      id: 'network-after-active',
      code: 'NET02',
      kind: 'self_action',
      title: '会员已生效，当前更像访问问题',
      body: '会员或 5TB 仍然存在，说明兑换权益已经生效。Gemini 页面异常通常需要从网络和服务状态继续排查。',
      recommendation: '先使用稳定网络重新登录和刷新，不要重复兑换或要求更换链接。',
      fallbackOutcomeId: 'active-service-boundary',
      fallbackLabel: '已经重新登录和刷新，仍没解决',
    },
    'third-party-boundary': {
      id: 'third-party-boundary',
      code: 'SCOPE01',
      kind: 'out_of_scope',
      title: '这是独立服务，不由会员兑换决定',
      body: 'API 配额、AI Studio、反重力和第三方客户端都有独立的账号、地区、配额或计费规则。',
      recommendation: '会员兑换不承诺解锁这些服务，也不能把这些服务不可用判断为兑换失败。',
      primaryAction: { label: '查看 Gemini 帮助', href: 'https://support.google.com/gemini/' },
    },
    'gemini-service-boundary': {
      id: 'gemini-service-boundary',
      code: 'SCOPE02',
      kind: 'out_of_scope',
      title: '当前更像 Gemini 网页或账号访问问题',
      body: '网页恢复方法仍然无效时，当前更像 Gemini 服务、账号访问或网络环境问题，不属于兑换链接状态异常。',
      recommendation: '无需更换或重复兑换链接。请通过 Gemini 官方帮助继续排查，或稍后使用稳定网络重试。',
      primaryAction: { label: '查看 Gemini 帮助', href: 'https://support.google.com/gemini/' },
    },
    'active-service-boundary': {
      id: 'active-service-boundary',
      code: 'SCOPE03',
      kind: 'out_of_scope',
      title: '会员权益仍在，兑换已经完成',
      body: '会员方案或 5TB 仍然存在，说明兑换链接已经正常生效；Gemini 页面或对话异常属于后续访问问题。',
      recommendation: '无需补发或重复兑换。请继续从网络、重新登录和谷歌服务状态排查。',
      primaryAction: { label: '查看会员方案', href: 'https://one.google.com/' },
    },
    'drop-needs-review': {
      id: 'drop-needs-review',
      code: 'DROP01',
      kind: 'seller_review',
      title: '权益可能已经提前结束',
      body: '活动权益的实际期限受官方账号状态和上游活动资格影响。网页不能自行判断是否符合售后条件。',
      recommendation: '复制摘要并把会员方案页或存储页完整截图发给卖家，按现有质保规则核实。',
    },
    'check-entitlement-first': {
      id: 'check-entitlement-first',
      code: 'CHECK01',
      kind: 'self_action',
      title: '先确认权益是否真的消失',
      body: '聊天或某个工具不能用，不等于会员已经消失。请先查看会员方案页和存储空间。',
      recommendation: '两处都看不到原权益时，再复制诊断摘要联系卖家。',
      primaryAction: { label: '打开会员方案页', href: 'https://one.google.com/' },
      fallbackOutcomeId: 'drop-needs-review',
      fallbackLabel: '已经检查，两处都看不到',
    },
    'existing-plan-warning': {
      id: 'existing-plan-warning',
      code: 'PLAN01',
      kind: 'self_action',
      title: '这个账号已有方案，继续可能覆盖或冲突',
      body: '已有会员、家庭组或受管理方案时，页面可能显示切换方案，也可能不符合活动资格。',
      recommendation: '优先使用没有现有方案且符合条件的其他账号；不要在不清楚影响时点击切换。',
      fallbackOutcomeId: 'account-self-service-complete',
      fallbackLabel: '已经换账号，仍没解决',
    },
    'account-check-first': {
      id: 'account-check-first',
      code: 'CHECK02',
      kind: 'self_action',
      title: '先检查账号当前状态',
      body: '打开会员方案、家庭组和付款设置，确认是否存在现有方案或付款资料。',
      recommendation: '只查看即可；不要把账号信息或截图中的邮箱发给陌生人。',
      primaryAction: { label: '检查会员方案', href: 'https://one.google.com/' },
      fallbackOutcomeId: 'account-self-service-complete',
      fallbackLabel: '已经检查，仍没解决',
    },
    'account-ready': {
      id: 'account-ready',
      code: 'OK03',
      kind: 'resolved',
      title: '这个账号更适合用于兑换',
      body: '当前未发现已有方案、家庭组或付款资料冲突。',
      recommendation: '保持登录这个账号，再打开卖家发来的原兑换链接。成功后检查权益并确认收货。',
      fallbackOutcomeId: 'account-self-service-complete',
      fallbackLabel: '重新打开链接后仍没解决',
    },
    'account-self-service-complete': {
      id: 'account-self-service-complete',
      code: 'ACT03',
      kind: 'seller_review',
      title: '账号条件排查完成',
      body: '当前结果更像账号资格、现有方案或付款资料限制，暂未显示兑换链接本身异常。请复制诊断摘要发回聊天。',
      recommendation: '自动客服会根据诊断编号继续引导换账号；没有合适账号且链接尚未激活时，再引导按双方协商一致申请退款。',
    },
    'payment-profile-closed-still-failed': {
      id: 'payment-profile-closed-still-failed',
      code: 'ACT04',
      kind: 'seller_review',
      title: '付款资料已处理，仍无法兑换',
      body: '你已经关闭不再使用的付款资料并重新登录重试，不需要再次关闭。当前仍更像账号资格限制，暂未显示兑换链接本身异常。',
      recommendation: '复制诊断摘要发回聊天；自动客服会继续引导换账号，确实无法解决时再按协商结果处理退款。',
    },
    'self-action-needs-review': {
      id: 'self-action-needs-review',
      code: 'REVIEW01',
      kind: 'seller_review',
      title: '地区排查已经完成',
      body: '如果账号和付款资料等已排查，请最后换一个没试过的可用节点，重新打开原链接；手头节点都试过仍失败，就不用反复尝试。',
      recommendation: '原链接仍未激活且没有合适的其他账号时，可选择「与卖家协商一致」申请退款，卖家核实后处理；网页本身不会操作订单。',
    },
  },
};
